import type { PermissionDetailsMap, UseFSEntryPermissionsProps } from '.';

/**
 * Human-readable metadata for each permission level used in the UI.
 */
const permissionDetails: PermissionDetailsMap = {
  read: {
    type: 'read',
    title: 'Tylko podgląd',
    description: 'Możesz tylko przeglądać pliki i katalogi, bez możliwości zmian.',
  },
  write: {
    type: 'write',
    title: 'Współpraca',
    description: 'dodawanie/usuwanie w katalogach, edycja treści plików',
  },
  edit: {
    type: 'edit',
    title: 'Pełny dostęp',
    description: 'Możesz przeglądać, dodawać, usuwać i w pełni edytować pliki oraz katalogi.',
  },
} as const;

/**
 * Resolve the current user's effective permission and provide helper metadata.
 *
 * Priority resolution:
 * - edit > write > read
 *
 * @param useFSEntryPermissionsProps - Props containing optional permission flags.
 * @returns An object with:
 * - permissionsArray: ordered list of all permission details (read, write, edit).
 * - currentPermission: details of the strongest effective permission.
 *
 * @example
 * const { currentPermission } = useFSEntryPermissions({
 *   permissions: { read: true, write: true, edit: false },
 * });
 */
export function useFSEntryPermissions(useFSEntryPermissionsProps: UseFSEntryPermissionsProps) {
  const { permissions } = useFSEntryPermissionsProps;

  const getCurrentPermission = () => {
    if (permissions?.edit) {
      return permissionDetails.edit;
    }
    if (permissions?.write) {
      return permissionDetails.write;
    }
    return permissionDetails.read;
  };

  return {
    permissionsArray: Object.values(permissionDetails),
    currentPermission: getCurrentPermission(),
  };
}
